import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { MessageCircle, Eye, EyeOff, Save, Send, Loader2, Info } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const WhatsappSettings = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const { toast } = useToast();
  const getHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
  }), []);

  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [testLoading, setTestLoading]   = useState(false);
  const [testMobile, setTestMobile]     = useState('');
  const [testTemplate, setTestTemplate] = useState('docket');
  const [activeTab, setActiveTab]       = useState('docket');
  const [showToken, setShowToken]       = useState(false);
  const [showInst, setShowInst]         = useState(false);

  const [form, setForm] = useState({
    wa_enabled:               false,
    wa_token:                 '',
    wa_instance_id:           '',
    wa_recipient_type:        'all',
    wa_admin_phone:           '',
    wa_template_text:         '',
    wa_tpl_booking_customer:  '',
    wa_tpl_booking_admin:     '',
    wa_tpl_dispatch_customer: '',
    wa_tpl_dispatch_admin:    '',
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    fetch(`${backendUrl}/api/whatsapp/settings`, { headers: getHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) setForm({
          wa_enabled:               data.wa_enabled === 'true',
          wa_token:                 data.wa_token || '',
          wa_instance_id:           data.wa_instance_id || '',
          wa_recipient_type:        data.wa_recipient_type || 'all',
          wa_admin_phone:           data.wa_admin_phone || '',
          wa_template_text:         data.wa_template_text || '',
          wa_tpl_booking_customer:  data.wa_tpl_booking_customer || '',
          wa_tpl_booking_admin:     data.wa_tpl_booking_admin || '',
          wa_tpl_dispatch_customer: data.wa_tpl_dispatch_customer || '',
          wa_tpl_dispatch_admin:    data.wa_tpl_dispatch_admin || '',
        });
      })
      .catch(() => toast({
        title: 'Error',
        description: 'Failed to load WhatsApp settings from server',
        variant: 'destructive',
      }))
      .finally(() => setLoading(false));
  }, [backendUrl, getHeaders, toast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, wa_enabled: form.wa_enabled ? 'true' : 'false' };
      const res = await fetch(`${backendUrl}/api/whatsapp/settings`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      toast({
        title: 'Settings Saved',
        description: 'WhatsApp settings and templates saved successfully.',
      });
    } catch (e) {
      toast({
        title: 'Save Failed',
        description: e.message || 'Failed to save WhatsApp settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testMobile.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a test mobile number.',
        variant: 'destructive',
      });
      return;
    }
    if (!form.wa_token && !form.wa_instance_id) {
      toast({
        title: 'API Credentials Required',
        description: 'Please enter your WhatsApp Token and Instance ID in API Credentials above before testing.',
        variant: 'destructive',
      });
      return;
    }
    setTestLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/whatsapp/send`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          mobile: testMobile.trim(),
          template_type: testTemplate,
          docket_no: 'STAVAN/000001',
          customer_name: 'Test Customer',
          token: form.wa_token,
          instance_id: form.wa_instance_id,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send test message');
      toast({
        title: 'Test WhatsApp Sent',
        description: `Message sent to ${testMobile.trim()} successfully!`,
      });
    } catch (e) {
      toast({
        title: 'Test Failed',
        description: e.message || 'Error sending test message',
        variant: 'destructive',
      });
    } finally {
      setTestLoading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-7 h-7 animate-spin text-accent-blue" />
    </div>
  );

  const RECIPIENT_OPTIONS = [
    { value: 'exporter', label: 'Exporter / Shipper only' },
    { value: 'importer', label: 'Importer / Receiver only' },
    { value: 'bill_to',  label: 'Billed To party only' },
    { value: 'all',      label: 'All (Exporter + Importer + Billed To)' },
  ];

  return (
    <div className="space-y-6 max-w-3xl" data-testid="whatsapp-settings-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <MessageCircle className="w-7 h-7 text-green-600" />
        <div>
          <h2 className="text-2xl font-bold text-text-dark font-manrope">WhatsApp Settings</h2>
          <p className="text-sm text-text-secondary font-ibm mt-0.5">
            Configure WhatsApp notifications for automatic docket messages via smswaapi.com
          </p>
        </div>
      </div>

      {/* Enable Toggle */}
      <Card className="bg-white border border-gray-100 shadow-sm p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-text-dark font-ibm">Enable WhatsApp Notifications</p>
            <p className="text-xs text-text-secondary font-ibm mt-0.5">
              When enabled, WhatsApp is auto-sent on docket creation to selected parties
            </p>
          </div>
          <Switch
            data-testid="wa-enabled-toggle"
            checked={form.wa_enabled}
            onCheckedChange={v => set('wa_enabled', v)}
          />
        </div>
        {!form.wa_enabled && (
          <div className="mt-3 flex items-center gap-2 text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
            <Info className="w-4 h-4 shrink-0" />
            WhatsApp is currently <strong>disabled</strong>. Toggle on to activate.
          </div>
        )}
      </Card>

      {/* Send WA To */}
      <Card className="bg-white border border-gray-100 shadow-sm p-5 space-y-4">
        <div>
          <h3 className="font-semibold text-text-dark font-ibm border-b border-gray-100 pb-2">Send WhatsApp To</h3>
          <p className="text-xs text-text-secondary font-ibm mt-2">
            Choose which party on the docket receives WhatsApp messages.
            Message is sent to the contact number of the branch selected in the docket form.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {RECIPIENT_OPTIONS.map(opt => (
            <label
              key={opt.value}
              data-testid={`wa-recipient-${opt.value}`}
              className={`flex items-center gap-3 border rounded-lg px-4 py-3 cursor-pointer transition-all select-none
                ${form.wa_recipient_type === opt.value
                  ? 'border-green-500 bg-green-50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}
            >
              <input
                type="radio"
                name="wa_recipient_type"
                value={opt.value}
                checked={form.wa_recipient_type === opt.value}
                onChange={() => set('wa_recipient_type', opt.value)}
                className="accent-green-600 w-4 h-4 shrink-0"
              />
              <span className={`text-sm font-ibm ${form.wa_recipient_type === opt.value ? 'text-green-700 font-semibold' : 'text-text-dark'}`}>
                {opt.label}
              </span>
            </label>
          ))}
        </div>
        <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            If no contact number is found on the selected branch, the system logs:
            &quot;WhatsApp not sent as contact details are not added&quot; with the employee name who created the docket.
          </span>
        </div>
      </Card>

      {/* API Credentials */}
      <Card className="bg-white border border-gray-100 shadow-sm p-5 space-y-4">
        <h3 className="font-semibold text-text-dark font-ibm border-b border-gray-100 pb-2">API Credentials</h3>

        {/* Token */}
        <div className="space-y-1.5">
          <Label className="text-text-dark font-ibm font-medium text-sm">
            Token <span className="text-xs text-green-600 font-ibm ml-1">(encrypted)</span>
          </Label>
          <div className="relative">
            <Input
              data-testid="wa-token"
              name="wa_api_token_field"
              autoComplete="new-password"
              type={showToken ? 'text' : 'password'}
              placeholder={form.wa_token === '***' ? 'Saved (hidden)' : 'Enter your smswaapi token'}
              value={form.wa_token === '***' ? '' : form.wa_token}
              onChange={e => set('wa_token', e.target.value)}
              className="pr-10 font-mono text-sm border-gray-200"
            />
            <button type="button" onClick={() => setShowToken(!showToken)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary">
              {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {form.wa_token === '***' && (
            <p className="text-xs text-green-600 font-ibm">Token is saved. Leave blank to keep existing.</p>
          )}
        </div>

        {/* Instance ID */}
        <div className="space-y-1.5">
          <Label className="text-text-dark font-ibm font-medium text-sm">
            Instance ID <span className="text-xs text-green-600 font-ibm ml-1">(encrypted)</span>
          </Label>
          <div className="relative">
            <Input
              data-testid="wa-instance-id"
              name="wa_instance_id_field"
              autoComplete="new-password"
              type={showInst ? 'text' : 'password'}
              placeholder={form.wa_instance_id === '***' ? 'Saved (hidden)' : 'Enter your instance ID'}
              value={form.wa_instance_id === '***' ? '' : form.wa_instance_id}
              onChange={e => set('wa_instance_id', e.target.value)}
              className="pr-10 font-mono text-sm border-gray-200"
            />
            <button type="button" onClick={() => setShowInst(!showInst)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary">
              {showInst ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {form.wa_instance_id === '***' && (
            <p className="text-xs text-green-600 font-ibm">Instance ID is saved. Leave blank to keep existing.</p>
          )}
        </div>

        {/* Admin WhatsApp Number(s) */}
        <div className="space-y-1.5 pt-2 border-t border-gray-100">
          <Label className="text-text-dark font-ibm font-medium text-sm">
            Admin Notification WhatsApp Number(s)
          </Label>
          <Input
            data-testid="wa-admin-phone"
            placeholder="e.g. 9825012345, 9898012345 (comma-separated for multiple)"
            value={form.wa_admin_phone}
            onChange={e => set('wa_admin_phone', e.target.value)}
            className="font-ibm text-sm border-gray-200"
          />
          <p className="text-xs text-text-secondary font-ibm">
            Admin alerts for new bookings and dispatches will be delivered to these WhatsApp numbers.
          </p>
        </div>
      </Card>

      {/* Message Templates Section */}
      <Card className="bg-white border border-gray-100 shadow-sm p-5 space-y-4">
        <div>
          <h3 className="font-semibold text-text-dark font-ibm border-b border-gray-100 pb-2">
            Configurable Message Templates
          </h3>
          <p className="text-xs text-text-secondary font-ibm mt-1">
            Customize automated messages for each stage. Use <strong>*bold*</strong> for WhatsApp styling.
          </p>
        </div>

        {/* Template Tabs */}
        <div className="flex flex-wrap gap-1.5 p-1 bg-gray-100 rounded-lg">
          {[
            { id: 'docket', label: '1. Docket Created' },
            { id: 'booking_customer', label: '2. Booking (Customer)' },
            { id: 'booking_admin', label: '3. Booking (Admin)' },
            { id: 'dispatch_customer', label: '4. Dispatch (Customer)' },
            { id: 'dispatch_admin', label: '5. Dispatch (Admin)' },
          ].map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md font-ibm transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-green-700 shadow-xs font-semibold'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Active Template Editor */}
        {(() => {
          const cfg = {
            docket: {
              key: 'wa_template_text',
              title: 'Docket Creation Message (Customer)',
              desc: 'Sent automatically to the customer when a new docket is generated. Includes live tracking link.',
              placeholders: [
                '{customer_name}', '{docket_no}', '{booking_date}',
                '{pickup_pincode}', '{destination_pincode}', '{packages}',
                '{service_type}', '{status}', '{docket_link}', '{tracking_link}'
              ],
              placeholderExample: 'Dear {customer_name},\n\nYour docket has been successfully generated.\n*DOCKET NO:* {docket_no}\n*BOOKING DATE:* {booking_date}\n*PICKUP:* {pickup_pincode}\n*DESTINATION:* {destination_pincode}\n*PACKAGES:* {packages}\n*TYPE:* {service_type}\n*STATUS:* {status}\n\nFind your docket here: {docket_link}\nFor tracking please visit: {tracking_link}\n\nThank you for choosing\nSTAVAN GROUP'
            },
            booking_customer: {
              key: 'wa_tpl_booking_customer',
              title: 'New Booking Inquiry Confirmation (Customer)',
              desc: 'Sent automatically to the customer when a new booking request is submitted.',
              placeholders: [
                '{customer_name}', '{inquiry_no}', '{from_location}',
                '{to_location}', '{vehicle_type}', '{packages}', '{weight}'
              ],
              placeholderExample: 'Dear {customer_name},\n\nThank you for your booking inquiry with STAVAN GROUP!\nWe have received your booking request *{inquiry_no}*.\n\n*ROUTE:* {from_location} to {to_location}\n*VEHICLE:* {vehicle_type}\n*PACKAGES:* {packages}\n*WEIGHT:* {weight} KG\n\nOur dispatch team is reviewing your request and will assign a vehicle shortly.\n\nThank you,\nSTAVAN GROUP'
            },
            booking_admin: {
              key: 'wa_tpl_booking_admin',
              title: 'New Booking Alert (Admin)',
              desc: 'Sent to Admin WhatsApp number(s) whenever a new booking inquiry is created.',
              placeholders: [
                '{customer_name}', '{inquiry_no}', '{from_location}',
                '{to_location}', '{vehicle_type}', '{packages}', '{weight}',
                '{contact_name}', '{contact_phone}'
              ],
              placeholderExample: '🚨 *NEW BOOKING INQUIRY*\n\n*INQUIRY NO:* {inquiry_no}\n*CUSTOMER:* {customer_name}\n*ROUTE:* {from_location} to {to_location}\n*VEHICLE:* {vehicle_type}\n*PACKAGES:* {packages}\n*WEIGHT:* {weight} KG\n*CONTACT:* {contact_name} ({contact_phone})\n\nPlease assign vehicle in Dispatch Control Tower.'
            },
            dispatch_customer: {
              key: 'wa_tpl_dispatch_customer',
              title: 'Dispatch Confirmation (Customer)',
              desc: 'Sent to customer when booking is dispatched with truck and driver assigned.',
              placeholders: [
                '{customer_name}', '{inquiry_no}', '{vehicle_no}',
                '{driver_name}', '{driver_phone}', '{trip_no}', '{docket_no}',
                '{from_location}', '{to_location}'
              ],
              placeholderExample: 'Dear {customer_name},\n\nYour booking *{inquiry_no}* has been confirmed & dispatched!\n\n*TRUCK NO:* {vehicle_no}\n*DRIVER:* {driver_name} ({driver_phone})\n*TRIP NO:* {trip_no}\n*DOCKET NO:* {docket_no}\n*ROUTE:* {from_location} to {to_location}\n\nThank you for choosing\nSTAVAN GROUP'
            },
            dispatch_admin: {
              key: 'wa_tpl_dispatch_admin',
              title: 'Dispatch Alert (Admin)',
              desc: 'Sent to Admin WhatsApp number(s) when booking is dispatched with truck, driver, and advance details.',
              placeholders: [
                '{customer_name}', '{inquiry_no}', '{vehicle_no}',
                '{driver_name}', '{driver_phone}', '{advance}', '{trip_no}',
                '{docket_no}', '{from_location}', '{to_location}'
              ],
              placeholderExample: '🚚 *DISPATCH CONFIRMATION*\n\nBooking *{inquiry_no}* has been dispatched.\n*TRIP NO:* {trip_no}\n*DOCKET NO:* {docket_no}\n*VEHICLE:* {vehicle_no}\n*DRIVER:* {driver_name} ({driver_phone})\n*ADVANCE PAID:* ₹{advance}\n*ROUTE:* {from_location} to {to_location}'
            }
          }[activeTab];

          return (
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-text-dark text-sm font-ibm">{cfg.title}</h4>
                  <p className="text-xs text-text-secondary font-ibm mt-0.5">{cfg.desc}</p>
                </div>
              </div>

              <div className="flex gap-1.5 flex-wrap text-xs font-ibm text-text-secondary items-center">
                <span className="font-semibold">Placeholders:</span>
                {cfg.placeholders.map(p => (
                  <code key={p} className="bg-gray-100 px-1.5 py-0.5 rounded text-text-dark font-mono text-[11px]">{p}</code>
                ))}
              </div>

              <Textarea
                rows={7}
                value={form[cfg.key] || ''}
                onChange={e => set(cfg.key, e.target.value)}
                className="font-ibm text-sm border-gray-200 resize-none"
                placeholder={cfg.placeholderExample}
              />
              <p className="text-xs text-text-secondary font-ibm text-right">
                Characters: {(form[cfg.key] || '').length}
              </p>
            </div>
          );
        })()}
      </Card>

      {/* Save Button */}
      <Button data-testid="wa-save-btn" onClick={handleSave} disabled={saving}
        className="bg-green-600 hover:bg-green-700 text-white font-ibm px-8">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        Save Settings
      </Button>

      {/* Test WhatsApp Feature */}
      <Card className="bg-green-50 border border-green-200 shadow-sm p-5 space-y-4">
        <div>
          <h3 className="font-semibold text-green-900 font-ibm">Test WhatsApp Notifications</h3>
          <p className="text-xs text-green-700 font-ibm mt-0.5">
            Select a message template to test and enter a mobile number to preview the WhatsApp alert.
          </p>
        </div>

        {(!form.wa_token || !form.wa_instance_id) && (
          <div className="flex items-center gap-2 p-3 text-xs bg-amber-100 text-amber-800 rounded-md border border-amber-300 font-ibm">
            <Info className="w-4 h-4 text-amber-600 shrink-0" />
            <span>
              <strong>Notice:</strong> WhatsApp Token or Instance ID is not entered yet. Enter them under <strong>API Credentials</strong> above and click <strong>Save Settings</strong> before testing live messages.
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-green-900 font-ibm">Template to Test</Label>
            <select
              value={testTemplate}
              onChange={e => setTestTemplate(e.target.value)}
              className="flex h-9 w-full rounded-md border border-green-300 bg-white px-3 py-1.5 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-green-500 font-ibm"
            >
              <option value="docket">1. Docket Creation (Customer)</option>
              <option value="booking_customer">2. Booking Inquiry (Customer)</option>
              <option value="booking_admin">3. Booking Alert (Admin)</option>
              <option value="dispatch_customer">4. Dispatch Confirmation (Customer)</option>
              <option value="dispatch_admin">5. Dispatch Alert (Admin)</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-green-900 font-ibm">Test Mobile Number</Label>
            <div className="flex gap-2">
              <Input
                data-testid="wa-test-mobile"
                placeholder="Enter 10-digit mobile (e.g. 9876543210)"
                value={testMobile}
                onChange={e => setTestMobile(e.target.value)}
                className="font-ibm border-green-300 bg-white text-sm"
              />
              <Button
                data-testid="wa-test-send-btn"
                onClick={handleTest}
                disabled={testLoading}
                className="bg-green-600 hover:bg-green-700 text-white font-ibm shrink-0 gap-1.5"
              >
                {testLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send Test
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default WhatsappSettings;
